// Operator › Templates (DC-03): the agent template catalog and its authoring, agency users only
// (anyone else gets the not-found page and no API call; the API answers them 404):
// - `/operator/templates`: the catalog;
// - `/operator/templates/$templateId`: a template, its versions and its edit proposals;
// - `/operator/templates/$templateId/versions/$versionId`: a version and its lifecycle actions.
import { createRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import type { Api } from "../api/client";
import type { MeResponse } from "../api/types";
import { isOperator } from "../features/company";
import { TemplateDetail } from "../features/operator/templates/template-detail";
import { TemplatesPage } from "../features/operator/templates/templates-page";
import { VersionDetail } from "../features/operator/templates/version-detail";
import { useSession } from "../features/use-feature";
import { NotFoundPage } from "../pages/status-pages";
import { OperatorShell, PlainShell } from "../shell/app-shell";
import { authedRoute } from "./_authed";

/** The operator shell around `page`, or the not-found page for anyone but an agency user. */
export function OperatorOnly({ api, page }: { api: Api; page: (me: MeResponse) => ReactNode }) {
  const { me } = useSession();
  if (!isOperator(me))
    return (
      <PlainShell me={me} api={api} current={null}>
        <NotFoundPage />
      </PlainShell>
    );
  return (
    <OperatorShell me={me} api={api}>
      {page(me)}
    </OperatorShell>
  );
}

export const operatorTemplatesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/templates",
  component: function TemplatesScreen() {
    const { api } = operatorTemplatesRoute.useRouteContext();
    return <OperatorOnly api={api} page={(me) => <TemplatesPage api={api} me={me} />} />;
  },
});

export const operatorTemplateRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/templates/$templateId",
  component: function TemplateScreen() {
    const { api } = operatorTemplateRoute.useRouteContext();
    const { templateId } = operatorTemplateRoute.useParams();
    return (
      <OperatorOnly
        api={api}
        page={(me) => <TemplateDetail key={templateId} api={api} me={me} templateId={templateId} />}
      />
    );
  },
});

export const operatorTemplateVersionRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/templates/$templateId/versions/$versionId",
  component: function TemplateVersionScreen() {
    const { api } = operatorTemplateVersionRoute.useRouteContext();
    const { templateId, versionId } = operatorTemplateVersionRoute.useParams();
    return (
      <OperatorOnly
        api={api}
        page={(me) => (
          <VersionDetail
            key={versionId}
            api={api}
            me={me}
            templateId={templateId}
            versionId={versionId}
          />
        )}
      />
    );
  },
});

export const templateOperatorRoutes = [
  operatorTemplatesRoute,
  operatorTemplateRoute,
  operatorTemplateVersionRoute,
] as const;
